import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { createOperatorInputController } from "../modules/teleop-input";
import { useRobotState } from "./useRobotState";
import type { OperatorInputController, OperatorInputState } from "../modules/teleop-input";
import type { OperatorProtocol } from "../modules/teleop-commands";
import { useTeleopXr } from "./useTeleopXr";
import type { UseTeleopXrResult } from "./useTeleopXr";
import type { XrVideoSource } from "../modules/teleop-xr";

/** Minimum spacing between automatic re-announcements of the selected source. */
const RESYNC_MIN_INTERVAL_MS = 1000;

export interface UseOperatorInputResult {
  controller: OperatorInputController;
  state: OperatorInputState;
  xr: UseTeleopXrResult;
}

/**
 * Owns the protocol-v1 operator input controller (teleop-input) for the page's lifetime:
 * starts its device listeners/stream timer on mount, gates streaming on `enabled`
 * (session joined/active), and wires the XR session (teleop-xr) into it.
 *
 * Also keeps the backend in sync: if its control-status shows no owner / a different
 * source than the one selected here (backend restarted, owner timed out), the selection
 * is re-announced so the operator-input stream is accepted again.
 */
export function useOperatorInput(
  protocol: OperatorProtocol,
  enabled: boolean,
  onStatus?: (text: string) => void,
  autoSelectXr = true,
  getXrVideoSources?: () => XrVideoSource[],
  videoLayoutKey?: string
): UseOperatorInputResult {
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const [controller] = useState(() =>
    createOperatorInputController({
      protocol,
      onStatus: (text) => onStatusRef.current?.(text),
    })
  );

  useEffect(() => {
    controller.start();
    return () => controller.stop();
  }, [controller]);

  useEffect(() => {
    controller.setEnabled(enabled);
  }, [controller, enabled]);

  const state = useStore(controller.store);
  const xr = useTeleopXr(controller, onStatus, autoSelectXr, getXrVideoSources, videoLayoutKey);

  const robotState = useRobotState();
  const backend = useStore(robotState.controlStatus, (s) => s.status);
  const liveBaseTrial = useStore(
    robotState.robotConfig,
    (s) => s.config?.robot?.control_mode === "live" || s.config?.robot?.control_mode === "mux-live"
  );
  useEffect(() => {
    if (liveBaseTrial && !enabled && state.activeId) controller.select(null);
  }, [liveBaseTrial, enabled, state.activeId, controller]);
  const lastResyncAt = useRef(0);
  useEffect(() => {
    // A live Panther trial requires an explicit source selection after a link
    // break or owner timeout, even if the keyboard is still selected locally.
    if (liveBaseTrial || !backend || !state.enabled || !state.activeId) return;
    const ownerFree = backend.owner_sid === null || backend.owner_sid === protocol.sid;
    if (!ownerFree || backend.active_source?.id === state.activeId) return;
    const now = Date.now();
    if (now - lastResyncAt.current < RESYNC_MIN_INTERVAL_MS) return;
    lastResyncAt.current = now;
    controller.resync();
  }, [backend, state.enabled, state.activeId, controller, protocol.sid, liveBaseTrial]);

  return { controller, state, xr };
}
